import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnDestroy,
  Signal,
} from '@angular/core';
import { AkitaLocationQuery } from '@app/akita/api/location/state/location.query';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaStartingPricesQuery } from '@app/akita/api/starting-prices/state/starting-prices.query';
import { AkitaStartingPricesService } from '@app/akita/api/starting-prices/state/starting-prices.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { StartingPriceModel } from '@app/shared/models/api/starting-price.model';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { combineLatest, map, Observable, of, Subject, Subscription } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { CategoryItemComponent } from './components/category-item/category-item.component';
import { categoryPickerMockData } from './category-picker.mock-data';
import { PopsyScrollComponent } from '../../../shared/components/popsy-scroller/popsy-scroller.component';

@Component({
  selector: 'app-category-picker',
  templateUrl: './category-picker.template.html',
  styleUrls: ['./category-picker.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    CategoryItemComponent,
    PopsyScrollComponent,
  ],
})
export class CategoryPickerComponent implements OnDestroy {
  @Input() public forceRow?: boolean | null = false;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;

  public readonly country$: Observable<string>;
  public readonly language$: Observable<string>;

  public readonly mockCategoryData = categoryPickerMockData;

  public categorySignal: Signal<Array<StartingPriceModel> | null>;

  public startingPrices$: Observable<Array<StartingPriceModel>>;
  public startingPricesPerCategory$: Observable<Array<StartingPriceModel> | null> =
    of(null);
  public readonly fetchingStartingPrices$: Observable<boolean>;
  public readonly startingPricesError$: Observable<ApiError | null>;

  public productCategories$: Observable<Array<string>>;
  public readonly fetchingProductOptions$: Observable<boolean>;
  public readonly productOptionsError$: Observable<ApiError | null>;

  public readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaLocationQuery: AkitaLocationQuery,
    private readonly translationService: TranslocoService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly zone: NgZone,
    private readonly akitaStartingPricesQuery: AkitaStartingPricesQuery,
    private readonly akitaStartingPricesService: AkitaStartingPricesService,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.language$ = this.translationService.langChanges$;
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.country$ = combineLatest([
      this.akitaRouterQuery.selectQueryParams(),
      this.akitaRouterQuery.selectRouteData(),
      this.akitaLocationQuery.selectCountry(),
    ]).pipe(
      map(([params, data, country]) =>
        `${params.country || data.country || country || 'US'}`.toUpperCase()
      )
    );

    this.startingPricesPerCategory$ = of(this.mockCategoryData);

    this.startingPrices$ = this.akitaStartingPricesQuery.selectStartingPrices();
    this.fetchingStartingPrices$ =
      this.akitaStartingPricesQuery.selectFetchingStartingPrices();
    this.startingPricesError$ = this.akitaStartingPricesQuery.selectStartingPricesError();

    // if we do not have statingPrices on the state
    if (!this.akitaStartingPricesQuery.getStartingPrices().length) {
      if (this.akitaRouterQuery.isBrowser) {
        this.zone.runOutsideAngular(this.fetchStartingPricesInBG.bind(this));
      } else {
        this.fetchStartingPricesInBG();
      }
    }

    this.productCategories$ = this.akitaProductsQuery.selectProductFilters().pipe(
      map((filters) => {
        // sort elements Phones Laptops Tablets Watches Audio Accessories
        let anyCategories: Array<string> = [];
        if (filters.category.length) {
          anyCategories = [...filters.category].filter((x) => x !== 'ACCESSORIES');
          anyCategories = [...anyCategories].filter((x) => x !== 'SMARTPHONE');
          anyCategories = [...anyCategories].filter((x) => x !== 'HEADPHONES');
          anyCategories = [...anyCategories].filter((x) => x !== 'WATCH');
          anyCategories = [...anyCategories].filter((x) => x !== 'GAMING');
          anyCategories.unshift('SMARTPHONE');
          anyCategories.push('ACCESSORIES');
          anyCategories.push('WATCH');
          anyCategories.push('HEADPHONES');
          anyCategories.push('GAMING');
        }
        return anyCategories;
      })
    );
    this.fetchingProductOptions$ =
      this.akitaProductsQuery.selectIsFetchingProductFilters();
    this.productOptionsError$ =
      this.akitaProductsQuery.selectErrorFetchingProductFilters();

    // if we do not have availableProductFilters on the state
    if (!this.akitaProductsQuery.getProductFilters()?.category?.length) {
      if (this.akitaRouterQuery.isBrowser) {
        this.zone.runOutsideAngular(this.fetchProductOptionsInBG.bind(this));
      } else {
        this.fetchProductOptionsInBG();
      }
    }

    this.categorySignal = toSignal(
      combineLatest([this.productCategories$, this.startingPrices$]).pipe(
        map(([cat, prices]) => {
          const output: Array<StartingPriceModel> = Array(0);
          const products: Array<StartingPriceModel> = JSON.parse(JSON.stringify(prices));
          const categories = JSON.parse(JSON.stringify(cat));

          if (categories.length && products.length) {
            for (let i = 0; i < categories.length; i++) {
              const sortedBiggerFirst = products.slice().sort(function (a, b) {
                return a.price.amount + b.price.amount;
              });
              const sortedSmallerFirst = products.slice().sort(function (a, b) {
                return a.price.amount - b.price.amount;
              });
              let biggerPrice = sortedBiggerFirst.find(
                (x) => x.category === categories[i] && x.inStock && x.brand === 'Apple'
              );
              let smallerPrice = sortedSmallerFirst.find(
                (x) => x.category === categories[i] && x.inStock && x.brand === 'Apple'
              );
              if (categories[i] === 'GAMING') {
                biggerPrice = sortedBiggerFirst.find(
                  (x) => x.category === categories[i] && x.inStock
                );
                smallerPrice = sortedSmallerFirst.find(
                  (x) => x.category === categories[i] && x.inStock
                );
              }

              if (biggerPrice && smallerPrice) {
                biggerPrice.price.amount = smallerPrice.price.amount;
                output.push(biggerPrice);
              }
            }
            return output;
          }
          return this.mockCategoryData;
        })
      ),
      { initialValue: this.mockCategoryData as Array<StartingPriceModel> }
    );

    this.startingPricesPerCategory$ = combineLatest([
      this.productCategories$,
      this.startingPrices$,
    ]).pipe(
      map(([cat, prices]) => {
        const output: Array<StartingPriceModel> = Array(0);
        const products: Array<StartingPriceModel> = JSON.parse(JSON.stringify(prices));
        const categories = JSON.parse(JSON.stringify(cat));

        if (categories) {
          for (let i = 0; i < categories.length; i++) {
            const sortedBiggerFirst = products.slice().sort(function (a, b) {
              return a.price.amount + b.price.amount;
            });
            const sortedSmallerFirst = products.slice().sort(function (a, b) {
              return a.price.amount - b.price.amount;
            });
            const biggerPrice = sortedBiggerFirst.find(
              (x) => x.category === categories[i] && x.inStock && x.brand === 'Apple'
            );
            const smallerPrice = sortedSmallerFirst.find(
              (x) => x.category === categories[i] && x.inStock && x.brand === 'Apple'
            );
            if (biggerPrice && smallerPrice) {
              biggerPrice.price.amount = smallerPrice.price.amount;
              output.push(biggerPrice);
            }
          }
        }

        return output;
      })
    );
  }

  private fetchStartingPricesInBG(): void {
    this.subscriptions.add(
      this.akitaStartingPricesService.findStartingPricesAsync(
        this.akitaRouterQuery.country
      )
    );
  }

  private fetchProductOptionsInBG(): void {
    this.subscriptions.add(this.akitaProductsService.getAvailableSearchOptionsAsync());
  }

  public trackByModel(index: number, category?: any | null): string {
    return (category as any)?.id || category || `${index}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
